import { t } from '@lingui/macro';
import * as Filestack from 'filestack-js';
import { MetadataOptions } from 'filestack-js/build/main/lib/api/file';
import { ClientOptions } from 'filestack-js/build/main/lib/client';
import {
  PickerFileMetadata,
  PickerInstance,
  PickerOptions,
  PickerStoreOptions,
  PickerUploadDoneCallback
} from 'filestack-js/build/main/lib/picker';

import { DEFAULT_FILESTACK_CNAME, FilestackCredentials } from '@api/v4/private/resources/fs_creds';
import { sendAction } from '@helpers/datadog-rum';

interface InitializeFilestackPickerOptions {
  filestackCredentials: FilestackCredentials;
  onUploadDone: PickerUploadDoneCallback;
  pickerOptions?: PickerOptions;
}

/**
 * Initialize Filestack picker
 * @param options InitializeFilestackPickerOptions
 * @returns PickerInstance
 */
export const initializeFilestackPicker = (options: InitializeFilestackPickerOptions): PickerInstance => {
  const { filestackCredentials, onUploadDone, pickerOptions = {} } = options;

  const { key, policy, signature } = filestackCredentials;
  const { onFileUploadFailed } = pickerOptions;

  const filestackOptions: ClientOptions = {
    security: {
      policy,
      signature
    },
    cname: DEFAULT_FILESTACK_CNAME
  };

  const filestack = Filestack.init(key, filestackOptions);

  const notifyEmptyFile = (): void => {
    Notify.create({
      title: t`Cannot upload empty file`,
      type: 'error'
    });
  };

  const handleFileSelected = (
    file: PickerFileMetadata
  ): void | Promise<unknown> => {
    if (file.size === 0) {
      notifyEmptyFile();
      // Reject selection so Filestack does not attempt upload.
      throw new Error('Cannot upload empty file');
    }

    if (typeof pickerOptions.onFileSelected === 'function') {
      return pickerOptions.onFileSelected(file);
    }

    return undefined;
  };

  const handleError = (file: PickerFileMetadata, error: Error): void => {
    if (error?.message?.indexOf('cancel') === -1) {
      sendAction('Error Uploading File to Filestack', {
        tags: { file, error },
        extra: { file, error }
      });
    }

    if (onFileUploadFailed) {
      onFileUploadFailed(file, error);
      return;
    }

    // Empty files are rejected in onFileSelected (toast already shown). If upload
    // still fails closed without that path, show the dedicated message once.
    if (error?.message === 'Cannot upload empty file') {
      return;
    }

    if (file?.size === 0) {
      notifyEmptyFile();
      return;
    }

    Notify.create({
      title: t`There was an error uploading your file. Please try again.`,
      type: 'error'
    });
  };

  const defaultOptions: PickerOptions = {
    onUploadDone,
    onFileSelected: handleFileSelected,
    onFileUploadFailed: handleError,
    disableThumbnails: true,
    maxFiles: 3000,
    fromSources: ['local_file_system', 'googledrive', 'dropbox', 'url'],
    globalDropZone: false,
    uploadConfig: {
      // default Filestack timeout for FII (intelligent ingestion) is only 1 minute
      timeout: 180000,
      intelligent: false, // Disabled due to ESC-9161
    }
  };

  const storageOptions = (): PickerStoreOptions => {
    // Printui & Workbench specific
    // Filestack 'Unsecured Workbench and Printui Cropping' account
    if (location.pathname.includes('/workbench/') || location.pathname == '/template_editor') {
      // Isolate into GCS bucket with short file retention, Filestack 'Workbench' account
      return {
        location: 'gcs',
        container: 'bf-storage-filestack-workbench-stage'
      };
    }
    // Filestack 'Boulder Lower' account
    // 10 day retention policy
    if (BFG.environment !== 'production') {
      return {
        location: 'gcs',
        container: 'bf-storage-filestack-stage'
      };
    }
    // Filestack 'Boulder Prouduction' account
    // 90 day retention & autoclass
    return {
      location: 'gcs',
      container: 'bf-storage-filestack-prod'
    };
  };

  // Keep caller onFileSelected out of the spread so we don't overwrite handleFileSelected.
  const { onFileSelected: _callerOnFileSelected, ...restPickerOptions } = pickerOptions;
  void _callerOnFileSelected;

  const completeOptions: PickerOptions = {
    ...defaultOptions,
    storeTo: storageOptions(),
    ...restPickerOptions,
    onFileSelected: handleFileSelected,
    onFileUploadFailed: handleError,
  };
  return filestack.picker(completeOptions);
};

interface InitializeFilestackMetadataOptions {
  filestackCredentials: FilestackCredentials;
  handle: string;
  metadataOptions?: MetadataOptions;
}

/**
 * Initialize Filestack metadata
 * @param options InitializeFilestackMetadataOptions
 * @returns  Promise<T>
 */
export const initializeFilestackMetadata = <T>(options: InitializeFilestackMetadataOptions): Promise<T> => {
  const { filestackCredentials, handle, metadataOptions = {} } = options;
  const { key, policy, signature } = filestackCredentials;

  const filestackOptions: ClientOptions = {
    security: {
      policy,
      signature
    },
    cname: DEFAULT_FILESTACK_CNAME
  };

  const defaultOptions: MetadataOptions = {
    filename: true,
    mimetype: true,
    width: true,
    height: true
  };

  const completeOptions: MetadataOptions = {
    ...defaultOptions,
    ...metadataOptions
  };

  const filestack = Filestack.init(key, filestackOptions);
  return filestack.metadata(handle, completeOptions);
};
